import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_BASE } from '@core/api';
import { Observable, map } from 'rxjs';
import {
  InventoryItemSuggestions,
  PhotoStationAcquireInput,
  PhotoStationCropFrame,
  PhotoStationError,
  PhotoStationErrorCode,
  PhotoStationItemReference,
  PhotoStationItemState,
  PhotoStationLockResult,
  PhotoStationMetadata,
  PhotoStationMetadataInput,
  PhotoStationOriginalPreview,
  PhotoStationWorkstation,
  sortedInventoryItemSuggestions,
} from '../models';

type Envelope<T> = { success: boolean; data: T; message: string };

/**
 * The photo-station browser API (RIEG-274). Every method returns the unwrapped
 * `data` and hands errors to the caller untouched — unlike the other services
 * here it neither notifies nor substitutes a neutral value, because the view
 * acts on the station's machine codes (ask before taking over a lock on
 * `workstation_in_other_tab`, show the selection on `workstation_in_use`).
 * Read them with {@link photoStationError}.
 */
@Injectable({ providedIn: 'root' })
export class PhotoStationService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = inject(API_BASE);

  /** The item's state. Changes no lock. */
  state(itemId: number): Observable<PhotoStationItemState> {
    return this.http
      .get<Envelope<PhotoStationItemState>>(this.url('state'), {
        params: { item_id: itemId },
      })
      .pipe(map(({ data }) => data));
  }

  /** The title and brand values of the item's project. */
  suggestions(itemId: number): Observable<InventoryItemSuggestions> {
    return this.http
      .get<Envelope<InventoryItemSuggestions>>(this.url('suggestions'), {
        params: { item_id: itemId },
      })
      .pipe(map(({ data }) => sortedInventoryItemSuggestions(data)));
  }

  /** Every workstation, with its status as seen from this browser. */
  workstations(browserId: string): Observable<PhotoStationWorkstation[]> {
    return this.http
      .get<Envelope<{ workstations: PhotoStationWorkstation[] }>>(
        this.url('workstations'),
        { params: { browser_id: browserId } }
      )
      .pipe(map(({ data }) => data.workstations));
  }

  acquire(input: PhotoStationAcquireInput): Observable<PhotoStationLockResult> {
    return this.post<PhotoStationLockResult>('acquire', input);
  }

  /** Keeps the view's lock alive. Never names the item. */
  heartbeat(
    workstation: string,
    viewId: string
  ): Observable<PhotoStationLockResult> {
    return this.post<PhotoStationLockResult>('heartbeat', {
      workstation,
      view_id: viewId,
    });
  }

  /** Ends the view's lock; answers normally when it was already gone. */
  release(
    workstation: string,
    viewId: string
  ): Observable<PhotoStationLockResult> {
    return this.post<PhotoStationLockResult>('release', {
      workstation,
      view_id: viewId,
    });
  }

  saveMetadata(
    itemId: number,
    metadata: PhotoStationMetadataInput
  ): Observable<PhotoStationMetadata> {
    return this.http
      .put<Envelope<PhotoStationMetadata>>(this.url('metadata'), {
        item_id: itemId,
        ...metadata,
      })
      .pipe(map(({ data }) => data));
  }

  /** The crop dialog's downscaled view of the photo's original. */
  originalPreview(photoId: number): Observable<PhotoStationOriginalPreview> {
    return this.http
      .get<Envelope<PhotoStationOriginalPreview>>(
        this.url(`photos/${photoId}/original`)
      )
      .pipe(map(({ data }) => data));
  }

  /**
   * Replaces the photo's crop with `frame` cut from its original and returns
   * the item's state. `expectedCropFileId` is the crop the dialog was opened
   * for; if it changed meanwhile the server refuses with `photo_changed`.
   */
  recrop(
    photoId: number,
    frame: PhotoStationCropFrame,
    expectedCropFileId: number
  ): Observable<PhotoStationItemState> {
    return this.post<PhotoStationItemState>(`photos/${photoId}/crop`, {
      ...frame,
      expected_crop_file_id: expectedCropFileId,
    });
  }

  /**
   * Deletes the photo whose crop is `fileId` from the item and returns the
   * item's state; if it was the main image, the newest remaining photo is
   * main now. A `fileId` no longer on the item, e.g. replaced by a crop
   * correction since the view loaded, is refused with `photo_not_found`.
   */
  deletePhoto(itemId: number, fileId: number): Observable<PhotoStationItemState> {
    return this.http
      .delete<Envelope<PhotoStationItemState>>(this.url(`files/${fileId}`), {
        params: { item_id: itemId },
      })
      .pipe(map(({ data }) => data));
  }

  private post<T>(path: string, body: object): Observable<T> {
    return this.http
      .post<Envelope<T>>(this.url(path), body)
      .pipe(map(({ data }) => data));
  }

  private url(path: string) {
    return `${this.apiBase}/api/component/photoStation/${path}`;
  }
}

/** Status, station machine code and message of a failed station call. */
export function photoStationError(error: unknown): PhotoStationError {
  if (!(error instanceof HttpErrorResponse)) {
    return { status: 0, code: null, message: null, item: null };
  }
  const body = error.error;
  const code = isPhotoStationErrorCode(body?.code) ? body.code : null;
  return {
    status: error.status,
    code,
    message: typeof body?.message === 'string' ? body.message : null,
    item:
      code === 'workstation_in_other_tab' && isItemReference(body?.item)
        ? { uid: body.item.uid, title: body.item.title ?? null }
        : null,
  };
}

function isItemReference(item: unknown): item is PhotoStationItemReference {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof (item as { uid?: unknown }).uid === 'string'
  );
}

const PHOTO_STATION_ERROR_CODES: readonly unknown[] = [
  'item_not_found',
  'project_access_denied',
  'workstation_in_use',
  'workstation_in_other_tab',
  'invalid_box',
  'photo_not_found',
  'photo_changed',
  'original_missing',
] satisfies readonly PhotoStationErrorCode[];

function isPhotoStationErrorCode(code: unknown): code is PhotoStationErrorCode {
  return PHOTO_STATION_ERROR_CODES.includes(code);
}
